import { yupResolver } from '@hookform/resolvers/yup'
import { getData } from 'country-list'
import { map } from 'lodash-es'
import { FC, useEffect, useState } from 'react'
import { FieldErrors, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import SelectField from '@/components/select-field'
import { Button, Card, Checkbox, Input } from '@/components/ui'
import { HtmlEditor } from '@/core/components/HtmlEditor'
import Loader from '../../../../common/components/Loader'
import {
  FestivalSettings,
  FestivalSettingsPayload,
  Languages,
} from '../../../../common/models'
import FormGroup from '../../../../core/components/FormGroup'
import { timezones } from '../../../../core/constants'
import {
  getGeneralSettings,
  updateGeneralSettings,
} from '../../../../core/services/festival-settings'
import { MessageBox } from '../../messages/MessageBox'

interface ISettingsValues {
  name?: string
  website?: string
  counterEnabled: boolean
  showFestivalDates?: boolean
  locationEnabled: boolean
  country?: string
  city?: string
  localTime?: string
  latitude?: string
  longitude?: string
  supportEmail?: string
  sendFromSupportEmail: boolean

  supportPhone?: string
  supportFirstName?: string
  supportLastName?: string

  // nextEditionDatePlaceholder?: string
  // invoiceEmail?: string
  // appStoreEnabled: boolean
  // appStoreUrl?: string
  // googleStoreEnabled: boolean
  // googleStoreUrl?: string
  // extraButtonEnabled: boolean
  // extraButtonLink?: string
  // ticketButtonEnabled: boolean
  // ticketButtonLink?: string
  // videoPortraitUrl?: string
  // videoLandscapeUrl?: string
  // radioEnabled: boolean
  // radioUrl?: string
  // redeemRateLimitRetries?: number
  // redeemRateLimitTtl?: number
  // sendInvoice: boolean

  contact?: {
    id?: string
    locale: string
    description: string
  }[]
}

interface GeneralSettingsProps {
  allLanguages: Languages[]
  hasEditPermission: boolean
}

export const GeneralSettings: FC<GeneralSettingsProps> = (
  props: GeneralSettingsProps,
) => {
  const { allLanguages, hasEditPermission } = props
  const { editionId, festivalName } = useParams()
  const [isCustomHTMLSelected, setIsCustomHTMLSelected] = useState<boolean[]>(
    [],
  )
  const [generalSettings, setGeneralSettings] = useState<FestivalSettings>()

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [submitLoading, setSubmitLoading] = useState<boolean>(false)

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        const resp = await getGeneralSettings()
        if (resp.status === 200) {
          setGeneralSettings(resp.data)
          setValue('name', resp.data.name)
          setValue('website', resp.data.website)
          setValue('counterEnabled', resp.data.counterEnabled)
          setValue('locationEnabled', resp.data.locationEnabled)
          setValue('country', resp.data.country)
          setValue('city', resp.data.city)
          setValue('localTime', resp.data.localTime)
          setValue('longitude', resp.data.longitude)
          setValue('latitude', resp.data.latitude)
          setValue('supportEmail', resp.data.supportEmail)
          setValue('supportPhone', resp.data.supportPhone)
          setValue('supportFirstName', resp.data.supportFirstName)
          setValue('supportLastName', resp.data.supportLastName)
          setValue('showFestivalDates', resp.data.showFestivalDates)
          setValue('sendFromSupportEmail', resp.data.sendFromSupportEmail)
          setValue(
            'contact',
            map(resp.data.
              contactInfoTexts, (lang) => {
              return {
                id: lang.id,
                locale: lang.locale,
                description: lang.text,
              }
            }),
          )
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
  }, [editionId, festivalName])

  const schema = yup.object().shape({
    name: yup.string().required('Field is required'),
    website: yup.string().required('Field is required'),
    counterEnabled: yup.boolean().required('Field is required'),
    locationEnabled: yup.boolean().required('Field is required'),
    country: yup.string().when('locationEnabled', {
      is: (val: boolean) => val,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.nullable().optional(),
    }),
    city: yup.string().when('locationEnabled', {
      is: (val: boolean) => val,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.nullable().optional(),
    }),
    localTime: yup.string().when('locationEnabled', {
      is: (val: boolean) => val,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.nullable().optional(),
    }),
    latitude: yup.string().when('locationEnabled', {
      is: (val: boolean) => val,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.nullable().optional(),
    }),
    longitude: yup.string().when('locationEnabled', {
      is: (val: boolean) => val,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.nullable().optional(),
    }),
    supportEmail: yup.string().when('locationEnabled', {
      is: (val: boolean) => val,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.nullable().optional(),
    }),
    supportPhone: yup.string().when('locationEnabled', {
      is: (val: boolean) => val,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.nullable().optional(),
    }),
    supportFirstName: yup.string().when('locationEnabled', {
      is: (val: boolean) => val,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.nullable().optional(),
    }),
    supportLastName: yup.string().when('locationEnabled', {
      is: (val: boolean) => val,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.nullable().optional(),
    }),
    contact: yup
      .array()
      .of(
        yup.object().shape({
          locale: yup.string().nullable().required('Field is required'),
          description: yup.string().required('Field is required'),
        }),
      )
      .when('locationEnabled', {
        is: (val: boolean) => val,
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.nullable().optional(),
      }),
    showFestivalDates: yup.boolean().required(),
    sendFromSupportEmail: yup.boolean().required(),
  })

  const {
    handleSubmit,
    register,
    formState: { errors },
    control,
    watch,
    setValue,
  } = useForm<ISettingsValues>({
    mode: 'onChange',
    resolver: yupResolver<ISettingsValues>(schema),
    defaultValues: {
      locationEnabled: false,
      showFestivalDates: false,
      counterEnabled: false,
      sendFromSupportEmail: false,
    },
  })

  const locationEnabled = watch('locationEnabled')

  const submit = async (values: ISettingsValues) => {
    const filteredWebsite = values?.website
      ?.replace('https://', '')
      ?.replace('http://', '')
      ?.replace('www.', '')
    const data: FestivalSettingsPayload = {
      name: values.name,
      website: filteredWebsite,
      locationEnabled: values.locationEnabled,
      country: values.country,
      counterEnabled: values.counterEnabled,
      city: values.city,
      localTime: values.localTime,
      latitude: values.latitude,
      longitude: values.longitude,
      supportEmail: values.supportEmail,
      showFestivalDates: values.showFestivalDates,
      sendFromSupportEmail: values.sendFromSupportEmail,
      supportPhone: values.supportPhone,
      supportFirstName: values.supportFirstName,
      supportLastName: values.supportLastName,
      contactInfoTexts:
        values.contact?.map((contact) => ({
          id: contact.id,
          locale: contact.locale,
          text: contact.description,
        })) || [],
    }

    try {
      setSubmitLoading(true)
      const { status } = await updateGeneralSettings(data)
      if (status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Update Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setSubmitLoading(false)
    }
  }

  const handleCancel = async () => {
    if (generalSettings) {
      setValue('name', generalSettings.name)
      setValue('website', generalSettings.website)
      setValue('counterEnabled', generalSettings.counterEnabled)
      setValue('locationEnabled', generalSettings.locationEnabled)
      setValue('country', generalSettings.country)
      setValue('city', generalSettings.city)
      setValue('localTime', generalSettings.localTime)
      setValue('longitude', generalSettings.longitude)
      setValue('latitude', generalSettings.latitude)
      setValue('supportEmail', generalSettings.supportEmail)
      setValue('supportPhone', generalSettings.supportPhone)
      setValue('supportFirstName', generalSettings.supportFirstName)
      setValue('supportLastName', generalSettings.supportLastName)
      setValue('showFestivalDates', generalSettings.showFestivalDates)
      setValue('sendFromSupportEmail', generalSettings.sendFromSupportEmail)
    }
  }

  const onError = (err: FieldErrors) => {
    console.log(err)
  }

  return (
    <Card className="bg-gradient-4 p-7.5">
      {submitLoading && <Loader />}

      <h3 className="text-title-xs text-monochrome-1 mb-6">
        Festival Settings
      </h3>

      <form
        id="settings-general-settings-form"
        onSubmit={handleSubmit(submit, onError)}
        className="space-y-6"
      >
        <div className="grid grid-cols-2 gap-4">
          <FormGroup label="Festival Name">
            <Input
              isInvalid={!!errors?.name?.message}
              {...register('name')}
              type="text"
              placeholder="Enter Festival Name"
              disabled={!hasEditPermission}
            />
          </FormGroup>
          <FormGroup label="Festival Website">
            <Input
              isInvalid={!!errors?.website?.message}
              {...register('website')}
              type="text"
              placeholder="Enter Website"
              disabled={!hasEditPermission}
            />
          </FormGroup>
        </div>
        <div className="grid grid-cols-3 gap-[25px] divide-x divide-monochrome-120">
          <FormGroup
            label="Festival Counter"
            direction="row"
            labelClassName="whitespace-nowrap"
            inputWrapperClassName="w-auto"
            wrapperClassName="w-auto flex-row-reverse justify-end"
            labelProps={{
              htmlFor: 'counterEnabled',
            }}
          >
            <Checkbox
              id="counterEnabled"
              checked={watch('counterEnabled')}
              onCheckedChange={(checked) => {
                setValue(
                  'counterEnabled',
                  checked === 'indeterminate' ? false : checked,
                )
              }}
              {...register('counterEnabled')}
              disabled={!hasEditPermission}
            />
          </FormGroup>
          <FormGroup
            label="Show Festival Dates"
            direction="row"
            labelClassName="whitespace-nowrap"
            inputWrapperClassName="w-auto"
            wrapperClassName="w-auto flex-row-reverse justify-end"
            labelProps={{
              htmlFor: 'showFestivalDates',
            }}
          >
            <Checkbox
              id="showFestivalDates"
              checked={watch('showFestivalDates')}
              onCheckedChange={(checked) => {
                setValue(
                  'showFestivalDates',
                  checked === 'indeterminate' ? false : checked,
                )
              }}
              {...register('showFestivalDates')}
              disabled={!hasEditPermission}
            />
          </FormGroup>
          <FormGroup
            label="Location"
            direction="row"
            labelClassName="whitespace-nowrap"
            inputWrapperClassName="w-auto"
            wrapperClassName="w-auto flex-row-reverse justify-end"
            labelProps={{
              htmlFor: 'locationEnabled',
            }}
          >
            <Checkbox
              id="locationEnabled"
              checked={watch('locationEnabled')}
              onCheckedChange={(checked) => {
                setValue(
                  'locationEnabled',
                  checked === 'indeterminate' ? false : checked,
                )
              }}
              {...register('locationEnabled')}
              disabled={!hasEditPermission}
            />
          </FormGroup>
        </div>
        <div className="grid grid-cols-3 gap-[25px]">
          {locationEnabled && (
            <>
              <FormGroup label="Country">
                <SelectField
                  outline
                  placeholder="Select Country"
                  options={map(getData(), (country, index) => ({
                    label: country.name,
                    value: country.name,
                  }))}
                  triggerClassName="!h-[44px] rounded-[16px]"
                  value={watch('country')}
                  onValueChange={(value) => {
                    setValue('country', value)
                  }}
                  {...register('country')}
                  disabled={!hasEditPermission}
                />
              </FormGroup>
              <FormGroup label="City" labelWrapperClassName="me-4">
                <Input
                  isInvalid={!!errors?.city?.message}
                  {...register('city')}
                  disabled={!hasEditPermission}
                />
              </FormGroup>
            </>
          )}
          <FormGroup label="Time zone Festival">
            <SelectField
              placeholder="Select Timezone"
              outline
              options={map(timezones, (timezone) => ({
                label: timezone.name,
                value: timezone.value,
              }))}
              triggerClassName="!h-[44px] rounded-[16px]"
              value={watch('localTime')}
              onValueChange={(value) => {
                setValue('localTime', value)
              }}
              {...register('localTime')}
              disabled={!hasEditPermission}
            />
          </FormGroup>
          <FormGroup label="Latitude" labelWrapperClassName="me-4">
            <Input
              isInvalid={!!errors?.latitude?.message}
              {...register('latitude')}
              disabled={!hasEditPermission}
            />
          </FormGroup>
          <FormGroup label="Longitude" labelWrapperClassName="me-4">
            <Input
              isInvalid={!!errors?.longitude?.message}
              {...register('longitude')}
              disabled={!hasEditPermission}
            />
          </FormGroup>
          <div className="space-y-3">
            <FormGroup label="Support Email" labelWrapperClassName="me-4">
              <Input
                isInvalid={!!errors?.supportEmail?.message}
                {...register('supportEmail')}
                disabled={!hasEditPermission}
              />
            </FormGroup>
            <FormGroup
              label="Send by this email"
              direction="row"
              labelWrapperClassName="whitespace-nowrap"
              inputWrapperClassName="w-auto"
              wrapperClassName="w-auto flex-row-reverse justify-end"
              labelProps={{
                htmlFor: 'sendFromSupportEmail',
              }}
            >
              <Checkbox
                id="sendFromSupportEmail"
                checked={watch('sendFromSupportEmail')}
                onCheckedChange={(checked) => {
                  setValue(
                    'sendFromSupportEmail',
                    checked === 'indeterminate' ? false : checked,
                  )
                }}
                {...register('sendFromSupportEmail')}
                disabled={!hasEditPermission}
              />
            </FormGroup>
          </div>
          <FormGroup label="Support Phone" labelWrapperClassName="me-4">
            <Input
              isInvalid={!!errors?.supportPhone?.message}
              {...register('supportPhone')}
              disabled={!hasEditPermission}
            />
          </FormGroup>
          <FormGroup label="Support First Name" labelWrapperClassName="me-4">
            <Input
              isInvalid={!!errors?.supportFirstName?.message}
              {...register('supportFirstName')}
              disabled={!hasEditPermission}
            />
          </FormGroup>
          <FormGroup label="Support Last Name" labelWrapperClassName="me-4">
            <Input
              isInvalid={!!errors?.supportLastName?.message}
              {...register('supportLastName')}
              disabled={!hasEditPermission}
            />
          </FormGroup>
        </div>

        <FormGroup label="Contact Intro">
          <div className="flex flex-col gap-2 w-full">
            {map(allLanguages, (language, index) => {
              return (
                <Card
                  className="flex flex-col p-3 gap-2 bg-monochrome-3-60 rounded-2xl"
                  key={language.id}
                >
                  <FormGroup
                    label="Language"
                    direction="column"
                    wrapperClassName="mb-0"
                  >
                    <Input
                      value={language.name}
                      readOnly
                      className="bg-monochrome-120"
                    />
                  </FormGroup>
                  <Input
                    {...register(`contact.${index}.id`)}
                    value={watch(`contact.${index}.id`) || language.id}
                    className="hidden"
                    readOnly
                  />

                  <Input
                    {...register(`contact.${index}.locale`)}
                    value={watch(`contact.${index}.locale`) || language.locale}
                    className="hidden"
                    readOnly
                  />

                  <HtmlEditor
                    variables={[]}
                    index={index}
                    name={`contact.${index}.description`}
                    control={control}
                    value={watch(`contact.${index}.description`) || ''}
                    onChange={(data) => {
                      setValue(`contact.${index}.description`, data)
                    }}
                    isError={!!errors?.contact?.[index]?.description?.message}
                  />
                </Card>
              )
            })}
          </div>
        </FormGroup>
      </form>

      <div className="flex justify-end gap-2 border-t border-monochrome-120 pt-4 [&>*]:w-[154px] mt-4">
        <Button
          onClick={handleCancel}
          variant="secondary"
          data-dismiss="modal"
          disabled={!hasEditPermission}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          form="settings-general-settings-form"
          type="submit"
          disabled={!hasEditPermission}
        >
          Save
        </Button>
      </div>

      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </Card>
  )
}
